import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useSetAtom, useAtomValue } from 'jotai';
import mergeWith from 'lodash/mergeWith';
import {
  currentMetricsMetaSelector,
  supportedMetricsSelector,
  getUserProfile,
} from '@store/common/selectors';
import { metricsStore } from '@src/globalStore/metricsStore';
import { selectedMetricSelector } from '@store/metricsChartContainer/selectors';
import { chartIntervalSelector } from '@store/trends/birdChart/selectors';
import { CURRENT_PRODUCT_MODE } from '@utils/constants';
import { tid } from '@utils/test';
import { selectedMetricsAtom, allMetricsAtom, metricOrderAtom } from './atoms';
import { allDimensionsListSelector } from '@store/common/dimensionList/selectors';
import { dimensionsCategory, dimensionsCategoryOrder } from '@utils/dimension/dimensionCategories';
import useSupportedFlowMetrics from '@src/hooks/metrics/useSupportedFlowMetrics';
import useGlobalExcludeMetricCategories from '@src/hooks/metrics/useGlobalExcludeMetricCategories';
import useSupportedMetrics from '@src/hooks/metrics/useSupportedMetrics';
import useDimensionsCategories from '../dimension-panel-hz/hooks/useDimensionsCategories';
import { ViewerSessionWrapper, VMTitle } from './styled';
import {
  getDimensionData,
  getMetricsData,
  getFlowMetricsData,
  getMandatoryDimensions,
  getExtractedMandatory,
  getOrderedMetrics,
  getHardCodeDimensionsInfo,
} from './utils';
import UserSessionsTable from './Table/TableIndex';
import AppViewerControls from './AppViewerControls';
import { getSecondaryMetric } from '@src/utils/metrics/metricHelpers';
import { InvalidIntervalType, useInvalidIntervalMessage } from '@hooks/useInvalidIntervalMessage';
import { useCurrentLimitation } from '@src/hooks/useCurrentLimitation';
import { useDrilldownMeta } from '@src/hooks/common/useDrilldownMeta';
import { globalFilterAtom } from '@src/atoms/page';
import { saveUserTimelineState } from '@utils/userTimelineStorage';

/**
 * [Remove 3 OOB metrics and high cardinality dimensions](https://conviva.atlassian.net/browse/HP-7194)
 * The hardcode dimensions will be removed from dimension list, but we can still use them on users table
 */
function useGetAllDimensions(customer_id: number) {
  const allDimensions = useSelector(allDimensionsListSelector) as any[];

  return useMemo(() => {
    /**
     * For some internal/test account, shouldn't append hardcode dimension when the dimension already in their dimension list
     */
    const dimensionsToAppend = getHardCodeDimensionsInfo(customer_id).filter((info) => {
      return allDimensions.every((it) => it.groupBy !== info.groupBy);
    });

    return [...dimensionsToAppend, ...allDimensions];
  }, [allDimensions, customer_id]);
}

function ViewerSessions() {
  const interval = useSelector(chartIntervalSelector);
  const { metricsCategoryMeta } = metricsStore.getState();
  const { useCaseKey, flowId } = useDrilldownMeta();
  const { customer_id } = useSelector(getUserProfile);
  const selectedMetricCardMetric: any = useSelector(selectedMetricSelector);
  const setSelectedMetrics: any = useSetAtom(selectedMetricsAtom);
  const setMetricOrder: any = useSetAtom(metricOrderAtom);
  const setAllMetrics = useSetAtom(allMetricsAtom);
  const allDimensions = useGetAllDimensions(customer_id) as any[];
  const supportedMetrics = useSelector(supportedMetricsSelector) as Record<string, any>;
  const currentMetricsMeta = useSelector(currentMetricsMetaSelector);
  const limitation = useCurrentLimitation();
  const excludeMetricCategories = useGlobalExcludeMetricCategories([]);
  const globalFilter = useAtomValue(globalFilterAtom);
  const selectedMetrics = useAtomValue(selectedMetricsAtom);

  const mandatoryDimensions = useMemo(
    () => getMandatoryDimensions(customer_id, selectedMetricCardMetric),
    [customer_id, selectedMetricCardMetric],
  );

  const supportedMetricsCategories = useSupportedMetrics({
    excludeCategories: excludeMetricCategories,
    limitation,
    allMetrics: supportedMetrics,
  });

  const { flowConfigMeta, flowMetricCategoryMeta } = useSupportedFlowMetrics({
    allMetrics: supportedMetrics,
    flowId: flowId,
  });

  const dimensionsCategories = useDimensionsCategories({
    allDimensions: allDimensions,
    selectedDimensionsList: [],
    excludeCategories: [],
    hiddenEmptyCategory: !!useCaseKey,
  });
  const currentDimensionsCategoryOrder = dimensionsCategoryOrder[CURRENT_PRODUCT_MODE];
  const dimensionsListCategory = dimensionsCategory[CURRENT_PRODUCT_MODE];
  const invalidIntervalMessage = useInvalidIntervalMessage({ type: InvalidIntervalType.USERS_TAB });

  useEffect(() => {
    let res = mandatoryDimensions;
    const secondaryMetric = getSecondaryMetric({ metricKey: selectedMetricCardMetric });
    if (secondaryMetric) {
      res = [...new Set([...res, secondaryMetric])];
    }
    setSelectedMetrics((prev: any) => {
      return [...new Set(prev.concat(res))];
    });
  }, [mandatoryDimensions, selectedMetricCardMetric, setSelectedMetrics]);

  useEffect(() => {
    const metricsData = getMetricsData(supportedMetricsCategories, metricsCategoryMeta);
    // If it's a flow drilldown, we don't need to show flow metrics, since flow metrics are already combined in metricsData
    const flowMetricsData = getFlowMetricsData(flowConfigMeta, flowMetricCategoryMeta);
    const dimensionData = getDimensionData({
      dimensionsCategories,
      currentDimensionsCategoryOrder,
      dimensionsListCategory,
    });

    const extractMandatory = getExtractedMandatory(
      mergeWith({}, dimensionData, metricsData, flowMetricsData, (a, b) =>
        Array.isArray(a) && Array.isArray(b) ? [...b, ...a] : undefined,
      ),
      mandatoryDimensions,
    );

    const orderedMetrics = getOrderedMetrics(currentMetricsMeta, allDimensions, customer_id);

    setMetricOrder(orderedMetrics);
    setAllMetrics(extractMandatory);
  }, [
    customer_id,
    allDimensions,
    flowMetricCategoryMeta,
    flowConfigMeta,
    metricsCategoryMeta,
    currentMetricsMeta,
    currentDimensionsCategoryOrder,
    dimensionsCategories,
    dimensionsListCategory,
    mandatoryDimensions,
    supportedMetricsCategories,
    selectedMetricCardMetric,
    setAllMetrics,
    setMetricOrder,
  ]);

  // Save global filter and selected metrics to localStorage for user timeline
  useEffect(() => {
    // Save whenever we have a global filter, even if selectedMetrics is empty initially
    // This ensures the filter is saved, and metrics will be saved once they populate
    if (globalFilter) {
      saveUserTimelineState({
        globalFilter,
        selectedMetrics: selectedMetrics || [],
      });
    }
  }, [globalFilter, selectedMetrics]);

  return (
    <ViewerSessionWrapper {...tid('app-viewer-sessions')}>
      {invalidIntervalMessage ? (
        invalidIntervalMessage
      ) : (
        <>
          <VMTitle>
            <div style={{ flex: 1, fontSize: '14px' }}>{`Subset of Users (up to 1500)`}</div>
            <AppViewerControls />
          </VMTitle>
          <UserSessionsTable interval={interval} />
        </>
      )}
    </ViewerSessionWrapper>
  );
}

export default ViewerSessions;
